import type { ReactNode } from 'react';
import {
  Modal,
  Paper,
  Group,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
  Text,
  Stack,
  Button,
  Divider,
} from '@mantine/core';
import { IconCopy, IconCheck } from '@tabler/icons-react';

interface DetailAction {
  label: string;
  icon: ReactNode;
  color?: string;
  onClick: () => void;
  hidden?: boolean;
}

interface DetailModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  recordId: string;
  children: ReactNode;
  actions?: DetailAction[];
  size?: string;
}

export function DetailModal({
  opened,
  onClose,
  title,
  recordId,
  children,
  actions,
  size = 'lg',
}: DetailModalProps) {
  const visibleActions = actions?.filter((a) => !a.hidden) || [];

  return (
    <Modal opened={opened} onClose={onClose} title={title} size={size}>
      <Stack gap="md">
        <Paper withBorder p="sm">
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Record ID:
            </Text>
            <Code style={{ flex: 1 }}>{recordId}</Code>
            <CopyButton value={recordId} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                  <ActionIcon
                    color={copied ? 'teal' : 'gray'}
                    variant="subtle"
                    onClick={copy}
                    size="sm"
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Paper>

        {children}

        {visibleActions.length > 0 && (
          <>
            <Divider />
            <Group justify="flex-end" gap="sm">
              {visibleActions.map((action) => (
                <Button
                  key={action.label}
                  variant="light"
                  color={action.color}
                  leftSection={action.icon}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
